/**
 * "How many panels do I need?" — Sabine RT60 estimate + role-based breakdown.
 * Intentionally coarse (preview); full product places panels in 3D.
 */

export type RoomType = 'mixing_studio' | 'home_theater' | 'hifi';
export type Construction = 'drywall' | 'concrete' | 'glass';

export interface PanelQuizInput {
  lengthM: number;
  widthM: number;
  heightM: number;
  roomType: RoomType;
  construction: Construction;
}

export interface PanelBreakdown {
  cornerBassTraps: number;
  firstReflectionPanels: number;
  ceilingClouds: number;
  totalPanels: number;
}

export interface PanelQuizResult {
  volumeM3: number;
  surfaceAreaM2: number;
  baselineRt60Sec: number;
  targetRt60Sec: number;
  requiredSabins: number;
  breakdown: PanelBreakdown;
}

/** Broadband average absorption coefficients for bare construction. */
export const CONSTRUCTION_ALPHA: Record<Construction, number> = {
  drywall: 0.08,
  concrete: 0.03,
  glass: 0.04,
};

/** Target midband RT60 by use case (seconds). */
export const TARGET_RT60: Record<RoomType, number> = {
  mixing_studio: 0.3,
  home_theater: 0.4,
  hifi: 0.45,
};

export function roomSurfaceAreaM2(lengthM: number, widthM: number, heightM: number): number {
  const floor = lengthM * widthM;
  const walls = 2 * (lengthM * heightM + widthM * heightM);
  return Math.round((2 * floor + walls) * 10) / 10;
}

/** Sabine: RT60 ≈ 0.161 · V / A  (V in m³, A in sabins / m² metric). */
export function sabineRt60(volumeM3: number, absorptionAreaM2: number): number {
  if (volumeM3 <= 0 || absorptionAreaM2 <= 0) return 0;
  return Math.round(((0.161 * volumeM3) / absorptionAreaM2) * 100) / 100;
}

export function requiredAbsorptionArea(volumeM3: number, targetRt60Sec: number): number {
  if (targetRt60Sec <= 0) return 0;
  return Math.round(((0.161 * volumeM3) / targetRt60Sec) * 10) / 10;
}

/**
 * Split needed absorption into role buckets. Counts are illustrative product
 * units (2×4 panels / corner traps / clouds), not a shopping cart.
 */
export function buildPanelBreakdown(
  requiredSabins: number,
  existingSabins: number,
  roomType: RoomType,
): PanelBreakdown {
  const deficit = Math.max(0, requiredSabins - existingSabins);
  // ~1.1 sabins per 2×4 broadband panel at midband (α≈0.8 · 0.72 m² · 2 faces rough)
  const panelSabins = 1.1;
  const totalNeeded = Math.max(6, Math.ceil(deficit / panelSabins));

  let cornerBassTraps = 4;
  let ceilingClouds = 2;
  let firstReflectionPanels = Math.max(4, totalNeeded - cornerBassTraps - ceilingClouds);

  if (roomType === 'home_theater') {
    cornerBassTraps = 4;
    ceilingClouds = 4;
    firstReflectionPanels = Math.max(6, totalNeeded - cornerBassTraps - ceilingClouds);
  } else if (roomType === 'mixing_studio') {
    cornerBassTraps = Math.min(8, Math.max(4, Math.round(totalNeeded * 0.35)));
    ceilingClouds = 2;
    firstReflectionPanels = Math.max(6, totalNeeded - cornerBassTraps - ceilingClouds);
  } else {
    // hifi — leaner treatment
    cornerBassTraps = 4;
    ceilingClouds = 2;
    firstReflectionPanels = Math.max(4, Math.min(10, totalNeeded - cornerBassTraps - ceilingClouds));
  }

  const totalPanels = cornerBassTraps + firstReflectionPanels + ceilingClouds;
  return { cornerBassTraps, firstReflectionPanels, ceilingClouds, totalPanels };
}

export function runPanelQuiz(input: PanelQuizInput): PanelQuizResult {
  const { lengthM, widthM, heightM, roomType, construction } = input;
  const volumeM3 = Math.round(lengthM * widthM * heightM * 10) / 10;
  const surfaceAreaM2 = roomSurfaceAreaM2(lengthM, widthM, heightM);
  const alpha = CONSTRUCTION_ALPHA[construction];
  const existingSabins = Math.round(surfaceAreaM2 * alpha * 10) / 10;
  const baselineRt60Sec = sabineRt60(volumeM3, existingSabins);
  const targetRt60Sec = TARGET_RT60[roomType];
  const requiredSabins = requiredAbsorptionArea(volumeM3, targetRt60Sec);
  const breakdown = buildPanelBreakdown(requiredSabins, existingSabins, roomType);

  return {
    volumeM3,
    surfaceAreaM2,
    baselineRt60Sec,
    targetRt60Sec,
    requiredSabins,
    breakdown,
  };
}
